/**
 * Aquafire configurator ("Build your Aquafire")
 *
 * Drives sections/aquafire-configurator.liquid: the customer picks a model, a size and upgrades, and the whole build
 * is added to the cart in one request. Each part is its own cart line (its real variant), tagged with a shared
 * "Build" property and a hidden "_build_id".
 *
 * Data comes from the <script type="application/json" data-afc-data> tags the section prints for each block, in
 * page order: models first, then each step followed by its options. Sizes are each model's variants plus the ganged
 * sizes from the section settings (two fireplaces side by side, e.g. 100" = 60" + 40") for models that can be ganged.
 *
 * Estimated ship dates: each part ships in its in-stock window (the product's custom.ship_time, else the section's) when
 * there is enough stock, else that window after the next incoming arrival (Shopify transfers and purchase orders) plus a
 * buffer, else within its lead time (custom.lead_time, else the section's). The build ships by its latest part.
 * Variants set to "continue selling when out of stock" stay available and show their date.
 *
 * Cart integration follows Impact's own product form (assets/theme.js, ProductForm): collect the sections to bundle
 * with "cart:prepare-bundled-sections", POST /cart/add.js, then fire "variant:add" (opens the cart drawer) and
 * "cart:change" (re-renders the drawer and cart count).
 */
(() => {
  if (window.customElements.get('aquafire-configurator')) {
    return;
  }

  // Size in inches from a variant name. "fits 20 in." wins, so the firebox "32 in. Firebox (fits 20 in. Aquafire)"
  // reads as 20. Otherwise the first number followed by an inch mark or unit: Small 20", 20 Inch, 16" Aquafire Lite.
  const FITS_PATTERN = /fits\s+(\d+)/i;
  const INCH_PATTERN = /(\d+)\s*(?:"|”|″|''|in\b|in\.|inch)/i;
  const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six'];

  const sizeOf = (text) => {
    const value = String(text || '');
    const match = value.match(FITS_PATTERN) || value.match(INCH_PATTERN);
    return match ? Number(match[1]) : null;
  };

  // Every number in a text: "52 in. Firebox (fits 40 in. Aquafire)" → [52, 40]
  const numbersIn = (text) => (String(text || '').match(/\d+/g) || []).map(Number);

  // Numbers in a picture's file name, so ".../AquafireBox_52_fire.png?v=1" → [52]. Long ids (Shopify's
  // _1aa1a8bd-93f7-… suffix on re-uploaded files) are dropped first so their digits don't read as sizes.
  const fileNumbers = (url) => {
    const name = String(url || '').split('?')[0].split('/').pop().replace(/\.[a-z0-9]+$/i, '');
    return numbersIn(name.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ' '));
  };

  const listOf = (text) => String(text || '').split(',').map((item) => item.trim().toLowerCase()).filter(Boolean);

  // "Label: value" lines → [[label, value], ...]
  const pairsOf = (text) => String(text || '').split('\n').map((line) => {
    const index = line.indexOf(':');
    return index > 0 ? [line.slice(0, index).trim(), line.slice(index + 1).trim()] : null;
  }).filter((pair) => pair && pair[0]);

  // "100: 60 + 40" lines → [{ total: 100, units: [60, 40] }, ...]
  const gangedSizesOf = (text) => String(text || '').split('\n').map((line) => {
    const match = line.match(/^\s*(\d+)\s*:\s*([\d\s+]+)$/);
    return match ? { total: Number(match[1]), units: match[2].split('+').map(Number).filter(Boolean) } : null;
  }).filter((entry) => entry && entry.units.length > 1);

  const formatMoney = (cents, format) => {
    const value = (Number(cents) || 0) / 100;
    const delimit = (decimals, thousands, decimal) => {
      const [whole, fraction] = value.toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };

    return String(format || '${{amount}}').replace(/<[^>]*>/g, '').replace(/\{\{\s*(\w+)\s*\}\}/, (_, placeholder) => {
      switch (placeholder) {
        case 'amount_no_decimals': return delimit(0, ',', '.');
        case 'amount_with_comma_separator': return delimit(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return delimit(0, '.', ',');
        case 'amount_with_space_separator': return delimit(2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return delimit(0, ' ', ',');
        case 'amount_with_apostrophe_separator': return delimit(2, '\'', '.');
        default: return delimit(2, ',', '.');
      }
    });
  };

  // Variant label to show next to an add-on: the first option value ("52 in. Firebox (fits 40 in. Aquafire)",
  // "40 Inch"), or nothing for single-variant products.
  const variantLabel = (variant) => (variant && variant.title !== 'Default Title' ? variant.options[0] : '');

  // Variant of a product for a size (null = any size). When several match, prefer the keyword, then stock.
  const matchVariant = (product, size, keyword) => {
    let variants = product.variants;

    if (size !== null) {
      variants = variants.filter((variant) => sizeOf(variant.title) === size);
    }

    if (variants.length > 1 && keyword) {
      const matching = variants.filter((variant) => variant.title.toLowerCase().includes(keyword.toLowerCase()));

      if (matching.length > 0) {
        variants = matching;
      }
    }

    return variants.find((variant) => variant.available) || variants[0] || null;
  };

  // Merge items that share a variant: [{ variant, quantity }]
  const mergeItems = (items) => {
    const merged = new Map();

    items.forEach((item) => {
      const existing = merged.get(item.variant.id);

      if (existing) {
        existing.quantity += item.quantity;
      } else {
        merged.set(item.variant.id, { ...item });
      }
    });

    return [...merged.values()];
  };

  // Product pages: the feature icons on the model cards and the model details section below the configurator
  // (assets/aquafire-model-details.js) come from each fireplace's product page, so they're only edited there. Only the
  // parts used are kept, in memory and in the browser's storage, so a returning visitor sees them at once; each page is
  // still fetched again once per visit to stay current.
  const productPages = (() => {
    const loads = new Map();
    const storageKey = (url) => `aquafire-product-page:${url}`;

    const extract = (html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const pick = (selector) => (doc.querySelector(`.product-info ${selector}`) || doc.querySelector(selector))?.outerHTML || '';

      // Impact puts adjacent "Offer" blocks in .product-info__offer-list and "Collapsible text" blocks in .accordion-group
      return { features: pick('.product-info__offer-list'), accordions: pick('.accordion-group') };
    };

    return {
      cached(url) {
        try {
          return JSON.parse(window.localStorage.getItem(storageKey(url)) || 'null');
        } catch (error) {
          return null;
        }
      },

      load(url) {
        if (!loads.has(url)) {
          const load = fetch(url, { headers: { Accept: 'text/html' } })
            .then((response) => {
              if (!response.ok) {
                throw new Error(`${url}: ${response.status}`);
              }

              return response.text();
            })
            .then((html) => {
              const parts = extract(html);

              try {
                window.localStorage.setItem(storageKey(url), JSON.stringify(parts));
              } catch (error) {
                // Storage can be full or blocked; the parts still show this visit.
              }

              return parts;
            });

          // Try again next time if it failed
          load.catch(() => loads.delete(url));
          loads.set(url, load);
        }

        return loads.get(url);
      }
    };
  })();

  window.AquafireProductPages = window.AquafireProductPages || productPages;

  // Videos (snippets/aquafire-configurator-video.liquid): a picture with a play button. The YouTube player only loads
  // when it's pressed, so nothing is fetched (or tracked) until then. reset() puts the picture back, which stops the
  // video: used when a popover closes or another model's video takes over.
  window.AquafireVideo = window.AquafireVideo || (() => {
    const play = (button) => {
      const frame = document.createElement('iframe');
      frame.className = 'afc-video__frame';
      frame.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(button.dataset.afcVideo)}?autoplay=1&rel=0&playsinline=1`;
      frame.title = button.dataset.afcVideoTitle || 'Video';
      frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen';
      frame.allowFullscreen = true;
      frame.referrerPolicy = 'strict-origin-when-cross-origin';
      frame.facade = button;
      button.replaceWith(frame);
      frame.focus();
    };

    const reset = (root) => {
      root.querySelectorAll('.afc-video__frame').forEach((frame) => frame.replaceWith(frame.facade));
    };

    document.addEventListener('click', (event) => {
      const button = event.target.closest?.('[data-afc-video]');

      if (button) {
        play(button);
      }
    });

    return { play, reset };
  })();

  const priceOf = (items) => items.reduce((sum, item) => sum + item.variant.price * item.quantity, 0);

  // A build's total: what its rows cost (free parts count for nothing)
  const totalOf = (rows) => rows.reduce((sum, row) => sum + row.price, 0);

  // Whether a variant can be bought in this quantity. Everything in the builder is set to continue selling when out of
  // stock (it shows a ship date instead); a variant set to stop selling that runs out is hidden, never shown as sold
  // out. "stock" is capped at 10 by the snippet, more than any build needs of one variant.
  const canBuy = (variant, quantity) => variant.available && !(variant.tracked && variant.policy !== 'continue' && variant.stock < quantity);

  // "2-3 days", "8–10 weeks", "15 business days", "3 weeks" → { min, max } in business days, or null
  const durationOf = (text) => {
    const match = String(text || '').match(/(\d+)\s*(?:(?:-|–|to)\s*(\d+))?\s*(business\s+days?|days?|weeks?|wks?)?/i);

    if (!match) {
      return null;
    }

    const perUnit = /^w/i.test(match[3] || '') ? 5 : 1;
    const min = Number(match[1]) * perUnit;
    return { min, max: Math.max(min, Number(match[2] || match[1]) * perUnit) };
  };

  const addBusinessDays = (date, days) => {
    const result = new Date(date);
    let left = days;

    while (left > 0) {
      result.setDate(result.getDate() + 1);

      if (result.getDay() !== 0 && result.getDay() !== 6) {
        left--;
      }
    }

    return result;
  };

  // The latest of several ship windows ({ backordered, start, end, days }); a window with no date wins, as nothing later
  // can be promised. "days" is the in-stock ship time in business days, kept while nothing is backordered.
  const latestShip = (windows) => windows.reduce((latest, ship) => {
    if (!latest) {
      return ship;
    }

    const backordered = latest.backordered || ship.backordered;
    const days = backordered ? null : { min: Math.max(latest.days.min, ship.days.min), max: Math.max(latest.days.max, ship.days.max) };

    if (!latest.end || !ship.end) {
      return { backordered, start: null, end: null, days };
    }

    return { backordered, start: ship.start > latest.start ? ship.start : latest.start, end: ship.end > latest.end ? ship.end : latest.end, days };
  }, null);

  // Line icons for the build summary (24 × 24, stroked), picked per option block in the theme editor.
  const ICONS = {
    flame: '<path d="M12 3c.9 3.4 5.5 5.6 5.5 10.3a5.5 5.5 0 0 1-11 0c0-2.5 1.3-4.1 2.5-5.3.3 1.7 1.2 2.8 2.4 3.2C11.6 8.4 12 5.6 12 3z"/>',
    firebox: '<rect x="3" y="6" width="18" height="12" rx="1.5"/><rect x="6.5" y="9" width="11" height="6" rx=".5"/>',
    plate: '<rect x="3" y="9" width="18" height="6" rx="1"/><path d="M7 12h10"/>',
    logs: '<rect x="3.5" y="9.5" width="17" height="4" rx="2" transform="rotate(-10 12 11.5)"/><rect x="3.5" y="14" width="17" height="4" rx="2" transform="rotate(8 12 16)"/>',
    drop: '<path d="M12 3.5c3.2 4.1 5.5 7.1 5.5 10.2a5.5 5.5 0 0 1-11 0c0-3.1 2.3-6.1 5.5-10.2z"/>',
    shield: '<path d="M12 3l7 2.8v5.4c0 4.4-2.9 7.8-7 9.8-4.1-2-7-5.4-7-9.8V5.8z"/>',
    scent: '<path d="M8 20c-1.6-1.6 1.6-3.4 0-5s1.6-3.4 0-5 1.6-3.4 0-5M12 20c-1.6-1.6 1.6-3.4 0-5s1.6-3.4 0-5 1.6-3.4 0-5M16 20c-1.6-1.6 1.6-3.4 0-5s1.6-3.4 0-5 1.6-3.4 0-5"/>',
    box: '<path d="M3.5 7.5L12 3.5l8.5 4v9L12 20.5l-8.5-4z"/><path d="M3.5 7.5L12 11.5l8.5-4M12 11.5v9"/>',
    bottle: '<path d="M10 3h4v3.2l2.2 2.6c.2.3.3.6.3.9V19.5a1.5 1.5 0 0 1-1.5 1.5H9a1.5 1.5 0 0 1-1.5-1.5V9.7c0-.3.1-.6.3-.9L10 6.2z"/><path d="M7.5 12.5h9"/>'
  };

  const iconElement = (name) => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'afc-review__icon');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '24');
    svg.setAttribute('height', '24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.3');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    svg.innerHTML = ICONS[name] || ICONS.box;
    return svg;
  };

  class AquafireConfigurator extends HTMLElement {
    connectedCallback() {
      if (this._initialized) {
        return;
      }

      this._initialized = true;
      this.moneyFormat = this.dataset.moneyFormat;
      this._parse();

      if (this.models.length === 0) {
        return;
      }

      this._buildSizeCards();
      this._buildCompareTable();
      this.querySelectorAll('[data-afc-share-action="share"]').forEach((button) => {
        button.hidden = !navigator.share;
      });
      this._readUrl();
      this._normalize();
      this._bind();
      this._render();
      this._observeSummary();
    }

    disconnectedCallback() {
      this._summaryObserver?.disconnect();
      this._printSheet?.remove();
      window.removeEventListener('beforeprint', this._onBeforePrint);
    }

    /* ---------------------------------------------------------------------------------------------------------------
     * Data
     * ------------------------------------------------------------------------------------------------------------ */

    _parse() {
      let settings = {};

      try {
        settings = JSON.parse(this.querySelector('script[data-afc-settings]')?.textContent || '{}');
      } catch (error) {
        console.error('[aquafire-configurator] Could not read section settings', error);
      }

      this.gangedSizes = gangedSizesOf(settings.gangedSizes);
      this.gangedNote = settings.gangedNote || '';
      this.gangedLabel = settings.gangedLabel || '';
      this.ship = {
        enabled: settings.ship?.enabled !== false,
        handlingMin: Number(settings.ship?.handlingMin) || 0,
        handlingMax: Math.max(Number(settings.ship?.handlingMax) || 0, Number(settings.ship?.handlingMin) || 0),
        arrivalBuffer: Number(settings.ship?.arrivalBuffer) || 0,
        lead: durationOf(settings.ship?.leadTime),
        property: settings.ship?.property || 'Estimated ship'
      };
      this._today = new Date();
      this._today.setHours(0, 0, 0, 0);
      this.models = [];
      this.steps = [];
      this.options = [];
      this.state = { model: null, sizeKey: null, picks: new Map(), quantities: new Map(), choices: new Map() };
      this._galleryStep = null;
      this._galleryMedia = [];

      let currentStep = null;

      this.querySelectorAll('script[data-afc-data]').forEach((script) => {
        let data;

        try {
          data = JSON.parse(script.textContent);
        } catch (error) {
          console.error('[aquafire-configurator] Could not read block data', error);
          return;
        }

        if (data.type === 'model') {
          data.element = script.closest('[data-afc-model]');
          data.input = data.element.querySelector('input');
          data.specs = pairsOf(data.specs);
          data.features = String(data.features || '').split('\n').map((line) => line.trim()).filter(Boolean);
          data.gangable = data.gangable !== false;
          data.iconsElement = data.element.querySelector('[data-afc-model-icons]');
          data.sizes = this._sizesFor(data);
          this.models.push(data);
        } else if (data.type === 'step') {
          data.element = script.closest('[data-afc-step]');
          data.models = listOf(data.models);
          data.options = [];
          data.noneElement = data.element.querySelector('[data-afc-option="none"]');
          data.mediaItems = [...data.element.querySelectorAll('[data-afc-media]')];
          data.mediaItems.forEach((item) => this._bindMediaCarousel(item));
          data.lastPicked = null;
          this.steps.push(data);
          this.state.picks.set(data.key, new Set());
          currentStep = data;
        } else if (data.type === 'option' && currentStep) {
          data.element = script.closest('[data-afc-option]');
          data.input = data.element.querySelector('input');
          data.step = currentStep;
          data.models = listOf(data.models);
          data.sizes = listOf(data.sizes).map(Number).filter(Boolean);
          data.requires = listOf(data.requires);
          data.excludes = listOf(data.excludes);
          data.extensionInches = Number(data.extensionInches) || 20;
          data.componentInches = Number(data.componentInches) || 20;
          data.components = data.components || [];
          data.maxPerInches = Number(data.maxPerInches) || 0;
          data.free = Boolean(data.included && data.free);
          data.chooseVariant = Boolean(data.chooseVariant);
          data.choiceElement = data.element.querySelector('[data-afc-choice]');
          data.choiceLabels = new Map([...data.element.querySelectorAll('[data-afc-choice-variant]')].map((button) => [
            button.dataset.afcChoiceVariant,
            button.querySelector('.afc-choice__label').textContent.trim()
          ]));
          data.quantityElement = data.element.querySelector('[data-afc-qty]');
          currentStep.options.push(data);
          this.options.push(data);
        }
      });
    }

    // A model's sizes: one per variant, then the ganged sizes it has every unit for (unless it can't be ganged).
    _sizesFor(model) {
      const singles = model.product.variants.map((variant) => ({
        key: String(sizeOf(variant.title) ?? `v${variant.id}`),
        total: sizeOf(variant.title),
        units: [{ variant, size: sizeOf(variant.title) }],
        title: variant.title,
        note: '',
        ganged: false
      }));

      const ganged = (model.gangable ? this.gangedSizes : []).map(({ total, units }) => {
        const unitEntries = units.map((size) => singles.find((entry) => entry.total === size)?.units[0]);

        if (unitEntries.some((unit) => !unit)) {
          return null;
        }

        const allSame = units.every((size) => size === units[0]);

        return {
          key: String(total),
          total,
          units: unitEntries,
          title: `${total}"`,
          note: allSame ? `${units.length} × ${units[0]}" ganged` : `${units.join('" + ')}" ganged`,
          ganged: true
        };
      }).filter(Boolean);

      return [...singles, ...ganged].map((entry) => {
        const items = mergeItems(entry.units.map((unit) => ({ product: model.product, variant: unit.variant, quantity: 1 })));
        const available = items.every((item) => canBuy(item.variant, item.quantity));

        return {
          ...entry,
          items,
          available,
          inStock: available && items.every((item) => !item.variant.tracked || item.variant.stock >= item.quantity),
          price: entry.units.reduce((sum, unit) => sum + unit.variant.price, 0)
        };
      });
    }

    _findModel(key) {
      const wanted = String(key || '').toLowerCase();
      return wanted ? this.models.find((model) => model.key === wanted) : undefined;
    }

    _findOption(key) {
      return this.options.find((option) => option.key === key);
    }

    // Keep the requested size when the model can sell it, else the first size that ships from stock, else the first
    // size that can be sold (backordered), else the first size.
    _pickSize(model, preferredKey) {
      return model.sizes.find((entry) => entry.key === preferredKey && entry.available)
        || model.sizes.find((entry) => entry.inStock)
        || model.sizes.find((entry) => entry.available)
        || model.sizes[0];
    }

    get _size() {
      return this.state.model.sizes.find((entry) => entry.key === this.state.sizeKey) || this.state.model.sizes[0];
    }

    _picks(step) {
      return this.state.picks.get(step.key);
    }

    _stepApplies(step) {
      return step.models.length === 0 || step.models.includes(this.state.model.key);
    }

    _isPicked(optionKey) {
      return this.steps.some((step) => this._stepApplies(step) && this._picks(step).has(optionKey));
    }

    _applies(option, ignoreRequirement) {
      const total = this._size?.total;

      if (option.models.length > 0 && !option.models.includes(this.state.model.key)) {
        return false;
      }

      if (option.sizes.length > 0 && !option.sizes.includes(total)) {
        return false;
      }

      // Options that depend on another one (the adapter plate needs a firebox) appear once it is picked.
      return option.requires.every((key) => key === ignoreRequirement || this._isPicked(key));
    }

    // What an option puts in the cart for the current model and size: { items, label, price, available } or null
    // when it doesn't apply to this size.
    _resolve(option) {
      const size = this._size;
      const units = size.units;
      const total = size.total;
      const pick = (product, inches) => matchVariant(product, inches, option.keyword);
      let items = [];
      let label = '';

      switch (option.match) {
        case 'unit': {
          const variant = pick(option.product, null);

          if (!variant) {
            return null;
          }

          items = [{ product: option.product, variant, quantity: units.length }];
          label = units.length > 1 ? `One per fireplace (× ${units.length})` : '';
          break;
        }

        case 'unit_first': {
          // One per fireplace: this product for the first, the extension product for each other one. The Direct Plumb
          // Kit with Vapor Pure, then kits without it, since an order gets one Vapor Pure.
          const firstVariant = pick(option.product, null);
          const restProduct = option.extension || option.product;
          const restVariant = units.length > 1 ? matchVariant(restProduct, null, option.extension ? '' : option.keyword) : null;

          if (!firstVariant || (units.length > 1 && !restVariant)) {
            return null;
          }

          items = [{ product: option.product, variant: firstVariant, quantity: 1 }];

          if (restVariant) {
            items = mergeItems([...items, { product: restProduct, variant: restVariant, quantity: units.length - 1 }]);
          }

          label = units.length > 1 ? `One per fireplace (× ${units.length})` : '';
          break;
        }

        case 'unit_size': {
          for (const unit of units) {
            const variant = pick(option.product, unit.size);

            if (!variant) {
              return null;
            }

            items.push({ product: option.product, variant, quantity: 1 });
          }

          items = mergeItems(items);
          label = items.map((item) => `${variantLabel(item.variant)}${item.quantity > 1 ? ` × ${item.quantity}` : ''}`).join(' + ');
          break;
        }

        case 'total_size': {
          if (total === null) {
            return null;
          }

          const exact = pick(option.product, total);

          if (exact) {
            items = [{ product: option.product, variant: exact, quantity: 1 }];
            label = variantLabel(exact);
            break;
          }

          if (!option.extension) {
            return null;
          }

          // No variant this wide: the widest one plus an extension for each extra width (72" firebox + 20" boxes).
          const keyword = (option.keyword || '').toLowerCase();
          const keywordVariants = option.product.variants.filter((variant) => keyword && variant.title.toLowerCase().includes(keyword));
          const widths = (keywordVariants.length > 0 ? keywordVariants : option.product.variants)
            .map((variant) => sizeOf(variant.title))
            .filter((inches) => inches !== null && inches < total);

          if (widths.length === 0) {
            return null;
          }

          const widest = Math.max(...widths);
          const base = pick(option.product, widest);
          const extension = matchVariant(option.extension, null, '');
          const count = Math.ceil((total - widest) / option.extensionInches);

          items = [{ product: option.product, variant: base, quantity: 1 }, { product: option.extension, variant: extension, quantity: count }];
          label = `${variantLabel(base)} + ${count} × ${option.extensionInches}" expansion`;
          break;
        }

        case 'length': {
          // One component per N inches of each fireplace's flame, in order: A, B, C. Ganged sizes get a set for each
          // fireplace, so 80" (40" + 40") is A, B + A, B.
          if (option.components.length === 0) {
            return null;
          }

          for (const unit of units) {
            if (!unit.size || unit.size % option.componentInches !== 0) {
              return null;
            }

            for (let index = 0; index < unit.size / option.componentInches; index++) {
              const component = option.components[index % option.components.length];
              const variant = matchVariant(component, null, '');

              if (!variant) {
                return null;
              }

              items.push({ product: component, variant, quantity: 1 });
            }
          }

          items = mergeItems(items);
          label = units.length > 1 ? `One set per fireplace: ${units.map((unit) => unit.size).join('" + ')}"` : `Sized for ${total}" of flame`;
          break;
        }

        default: {
          // The variant the customer picked on the card (Tank Guard: 2 or 6 sets), while it can be bought
          const chosenId = option.chooseVariant ? this.state.choices.get(option.key) : null;
          const chosen = chosenId && option.product.variants.find((candidate) => String(candidate.id) === chosenId && canBuy(candidate, 1));
          const variant = chosen || pick(option.product, null);

          if (!variant) {
            return null;
          }

          items = [{ product: option.product, variant, quantity: 1 }];
          label = option.chooseVariant ? option.choiceLabels.get(String(variant.id)) || variantLabel(variant) : '';
        }
      }

      // Options the customer sets a quantity for (Scent Diffuser): one product, as many as they chose.
      const range = this._quantityRange(option);

      if (range && items.length === 1) {
        const quantity = this._quantity(option);
        items = [{ ...items[0], quantity }];
        label = quantity > 1 ? `× ${quantity}` : label;
      }

      return { items, label, price: priceOf(items), available: items.every((item) => canBuy(item.variant, item.quantity)) };
    }

    // When one cart item ships: { backordered, start, end } (no dates when nothing is known).
    _shipOf(item) {
      const { product, variant, quantity } = item;
      const handling = durationOf(product.shipTime) || { min: this.ship.handlingMin, max: this.ship.handlingMax };
      const window = (from, days, backordered) => ({
        backordered,
        start: addBusinessDays(from, days.min),
        end: addBusinessDays(from, days.max),
        days: backordered ? null : days
      });

      if (!variant.tracked || variant.stock >= quantity) {
        return window(this._today, handling, false);
      }

      if (variant.next_incoming_date) {
        const [year, month, day] = variant.next_incoming_date.split('-').map(Number);
        const arrival = new Date(year, month - 1, day);
        const buffer = this.ship.arrivalBuffer;
        return window(arrival > this._today ? arrival : this._today, { min: buffer + handling.min, max: buffer + handling.max }, true);
      }

      const lead = durationOf(product.leadTime) || this.ship.lead;
      return lead ? window(this._today, lead, true) : { backordered: true, start: null, end: null };
    }

    _shipOfItems(items) {
      return latestShip(items.map((item) => this._shipOf(item)));
    }

    _formatShip(ship) {
      if (!ship?.end) {
        return '';
      }

      const format = (date) => date.toLocaleDateString(document.documentElement.lang || undefined, { month: 'short', day: 'numeric' });
      return ship.start.getTime() === ship.end.getTime() ? format(ship.end) : `${format(ship.start)} – ${format(ship.end)}`;
    }

    // Quantity limits for options with "up to one per N inches" set: at least one, up to one per N inches of each
    // fireplace (a 16" fireplace still takes one). One per fireplace is recommended and the default.
    _quantityRange(option) {
      if (!option.maxPerInches) {
        return null;
      }

      const units = this._size.units;
      const max = units.reduce((sum, unit) => sum + Math.max(1, Math.floor((unit.size || 0) / option.maxPerInches)), 0);
      return { min: 1, max, recommended: Math.min(units.length, max) };
    }

    _quantity(option) {
      const range = this._quantityRange(option);

      if (!range) {
        return 1;
      }

      const chosen = this.state.quantities.get(option.key) ?? range.recommended;
      return Math.min(Math.max(chosen, range.min), range.max);
    }

    _optionState(option) {
      if (!this._applies(option)) {
        return { visible: false };
      }

      const resolved = this._resolve(option);

      if (!resolved) {
        return { visible: false };
      }

      // Hidden, not shown as sold out, when it can't be bought, or when something that comes with it can't
      // (Lite firebox → adapter plate)
      if (!resolved.available) {
        return { visible: false };
      }

      const blocker = this.options.find((other) => {
        if (!other.included || !other.requires.includes(option.key) || !this._applies(other, option.key)) {
          return false;
        }

        const companion = this._resolve(other);
        return companion && !companion.available;
      });

      if (blocker) {
        return { visible: false };
      }

      // Can't be combined with something picked (ClearShield with logs): shown faded, with the reason, and left out
      const conflicts = this._conflicts(option);

      if (conflicts.length > 0) {
        return { visible: true, enabled: false, resolved, conflicts };
      }

      return { visible: true, enabled: true, resolved };
    }

    // Picked options this one isn't compatible with. The option with the rule gives way: picking logs drops the
    // ClearShield, never the other way round.
    _conflicts(option) {
      return this.options.filter((other) => option.excludes.includes(other.key) && this._isPicked(other.key));
    }

    // Picked options that picking this one would drop (on the logs card while a ClearShield is picked)
    _wouldRemove(option) {
      return this.options.filter((other) => other.excludes.includes(option.key) && this._isPicked(other.key));
    }

    _optionNote(option, state, active) {
      const names = (options) => options.map((other) => other.name).join(' and ');

      if (state.conflicts) {
        return `Not compatible with ${names(state.conflicts)}.`;
      }

      const removed = active ? [] : this._wouldRemove(option);
      return removed.length > 0 ? `Not compatible with ${names(removed)}, which will be removed.` : '';
    }

    _isActive(option, state = this._optionState(option)) {
      return Boolean(state.enabled) && (option.included || this._picks(option.step).has(option.key));
    }

    _stepVisible(step) {
      return this._stepApplies(step) && step.options.some((option) => this._optionState(option).visible);
    }

    // Drop picks that no longer apply after a model, size or pick change, and fill required steps. Repeated because
    // dropping one pick can invalidate another that requires it.
    _normalize() {
      for (let pass = 0; pass < 4; pass++) {
        let changed = false;

        this.steps.forEach((step) => {
          const picks = this._picks(step);

          if (!this._stepVisible(step)) {
            changed = changed || picks.size > 0;
            picks.clear();
            return;
          }

          picks.forEach((key) => {
            const option = step.options.find((candidate) => candidate.key === key);

            if (!option || option.included || !this._optionState(option).enabled) {
              picks.delete(key);
              changed = true;
            }
          });

          if (step.mode === 'single' && !step.hasNone && picks.size === 0) {
            const first = step.options.find((option) => !option.included && this._optionState(option).enabled);

            if (first) {
              picks.add(first.key);
              changed = true;
            }
          }
        });

        if (!changed) {
          break;
        }
      }
    }

    // Everything in the build, as summary rows; each row's items are what goes in the cart.
    _rows() {
      const size = this._size;
      const fireplaceItems = size.items;
      const rows = [{
        part: 'fireplace',
        icon: 'flame',
        name: this.state.model.name,
        label: size.ganged ? `${size.title} · ${size.note}` : size.title,
        items: fireplaceItems,
        price: priceOf(fireplaceItems)
      }];

      this.steps.forEach((step) => {
        if (!this._stepVisible(step)) {
          return;
        }

        step.options.forEach((option) => {
          const state = this._optionState(option);

          if (this._isActive(option, state)) {
            const { label, items, price } = state.resolved;

            // Free parts (the Vapor Pure with a Pro or a Direct Plumb Kit) count for nothing; value is what they're worth
            rows.push({ part: step.key, icon: option.icon, name: option.name, label, items, price: option.free ? 0 : price, value: price, free: option.free });
          }
        });
      });

      return rows;
    }

    get _buildName() {
      const size = this._size;
      return size?.total ? `${this.state.model.name} ${size.total}"` : this.state.model.name;
    }

    /* ---------------------------------------------------------------------------------------------------------------
     * URL (shareable builds: ?model=pro&size=100&firebox=firebox&extras=clearshield,diffuser)
     * ------------------------------------------------------------------------------------------------------------ */

    get _storageKey() {
      return `aquafire-configurator:${window.location.pathname}`;
    }

    _readUrl() {
      let params = new URLSearchParams(window.location.search);

      if (!params.has('model')) {
        try {
          const saved = window.localStorage.getItem(this._storageKey);

          if (saved) {
            params = new URLSearchParams(saved);
          }
        } catch (error) {
          // Storage can be blocked (private mode); the default build is used instead.
        }
      }

      const model = this._findModel(params.get('model')) || this._findModel(this.dataset.defaultModel) || this.models[0];

      this.state.model = model;
      this.state.sizeKey = this._pickSize(model, params.get('size'))?.key ?? null;

      this.steps.forEach((step) => {
        listOf(params.get(step.key)).forEach((value) => {
          // "diffuser:3" = option and quantity; "tank-guard:53045063221568" = option and the variant picked on its card
          const [key, quantity] = value.split(':');
          const option = step.options.find((candidate) => candidate.key === key && !candidate.included);

          if (!option) {
            return;
          }

          if (option.chooseVariant && quantity) {
            this.state.choices.set(key, quantity);
          } else if (option.maxPerInches && Number(quantity) > 0) {
            this.state.quantities.set(key, Math.floor(Number(quantity)));
          }

          if (step.mode === 'single') {
            this._picks(step).clear();
          }

          this._picks(step).add(key);
        });
      });
    }

    _writeUrl() {
      const params = new URLSearchParams(window.location.search);

      params.set('model', this.state.model.key);
      params.set('size', this._size.key);

      this.steps.forEach((step) => {
        const values = [...this._picks(step)].map((key) => {
          const option = this._findOption(key);
          if (option?.chooseVariant && this.state.choices.has(key)) {
            return `${key}:${this.state.choices.get(key)}`;
          }

          return option && option.maxPerInches && this.state.quantities.has(key) ? `${key}:${this._quantity(option)}` : key;
        });

        if (values.length > 0) {
          params.set(step.key, values.join(','));
        } else {
          params.delete(step.key);
        }
      });

      window.history.replaceState(window.history.state, '', `${window.location.pathname}?${params.toString()}${window.location.hash}`);

      try {
        window.localStorage.setItem(this._storageKey, params.toString());
      } catch (error) {
        // Storage can be blocked (private mode); the link still holds the build.
      }
    }

    /* ---------------------------------------------------------------------------------------------------------------
     * Events
     * ------------------------------------------------------------------------------------------------------------ */

    _bind() {
      this.addEventListener('change', this._onChange.bind(this));
      this.addEventListener('click', this._onClick.bind(this));

      // Phone pictures: the photo is also set as its slide's background (see _blendSlide), once it has loaded. "load"
      // doesn't bubble, so listen while it travels down.
      this.addEventListener('load', (event) => this._blendSlide(event.target), true);
      this.querySelectorAll('.afc-step__media-image').forEach((img) => this._blendSlide(img));

      // A popover's video stops when it closes ("close" doesn't bubble, so listen while it travels down)
      this.addEventListener('close', (event) => window.AquafireVideo.reset(event.target), true);

      // Printing the page (button or Cmd/Ctrl+P) prints the current build sheet
      this._onBeforePrint = () => this._renderPrintSheet();
      window.addEventListener('beforeprint', this._onBeforePrint);
    }

    // Product photos are shot on white and should take on the picture panel's gray. Blending the <img> into the panel
    // (mix-blend-mode) doesn't work in iPhone Safari once the picture sits in a swipeable, scroll-snapping track: the
    // white stays. So the slide itself paints the photo over its own gray with background-blend-mode, which is a single
    // paint of one element and doesn't depend on how the browser layers the page. The <img> stays for screen readers,
    // the pictures' sizes and the desktop gallery; it's only hidden once the slide shows the photo.
    _blendSlide(img) {
      const slide = img instanceof HTMLImageElement && img.classList.contains('afc-step__media-image') ? img.closest('.afc-step__media-slide') : null;
      const src = slide && img.complete && (img.currentSrc || img.src);

      if (!src) {
        return;
      }

      slide.style.setProperty('--afc-slide-image', `url("${src.replace(/"/g, '%22')}")`);
      slide.classList.add('is-blended');
    }

    _onChange(event) {
      const input = event.target;

      if (!(input instanceof HTMLInputElement)) {
        return;
      }

      if (input.closest('[data-afc-model]')) {
        const model = this._findModel(input.value);

        if (model && model !== this.state.model) {
          const sizeKey = this.state.sizeKey;
          this.state.model = model;
          this.state.sizeKey = this._pickSize(model, sizeKey)?.key ?? null;
          this._galleryStep = null;
        }
      } else if (input.closest('[data-afc-size]')) {
        this.state.sizeKey = input.value;
        this._galleryStep = null;
      } else {
        const stepElement = input.closest('[data-afc-step]');
        const step = stepElement && this.steps.find((candidate) => candidate.key === stepElement.dataset.afcStep);

        if (!step) {
          return;
        }

        const picks = this._picks(step);

        if (step.mode === 'single') {
          picks.clear();

          if (input.value !== 'none') {
            picks.add(input.value);
          }
        } else if (input.checked) {
          picks.add(input.value);
        } else {
          picks.delete(input.value);
        }

        if (input.value !== 'none' && input.checked) {
          step.lastPicked = input.value;
        }

        // Desktop: the main picture follows the step just changed, as the step's own picture does on phones
        this._galleryStep = window.matchMedia('(min-width: 1000px)').matches ? step : null;
      }

      this._normalize();
      this._render();
    }

    _onClick(event) {
      // Variant choice inside an option card (Tank Guard: 2 or 6 sets): picks that variant, without toggling the card
      const choiceGroup = event.target.closest('[data-afc-choice]');

      if (choiceGroup) {
        event.preventDefault();
        const choice = event.target.closest('[data-afc-choice-variant]');
        const option = choice && this._findOption(choiceGroup.closest('[data-afc-option]')?.dataset.afcOption);

        if (option) {
          this.state.choices.set(option.key, choice.dataset.afcChoiceVariant);
          this._render();
        }

        return;
      }

      // Quantity stepper inside an option card: the card is a <label>, so stop the click from toggling it.
      const stepper = event.target.closest('[data-afc-qty]');

      if (stepper) {
        event.preventDefault();
        const button = event.target.closest('[data-afc-qty-step]');
        const option = button && this._findOption(stepper.closest('[data-afc-option]')?.dataset.afcOption);

        if (option && !button.disabled) {
          const range = this._quantityRange(option);
          const quantity = this._quantity(option) + Number(button.dataset.afcQtyStep);
          this.state.quantities.set(option.key, Math.min(Math.max(quantity, range.min), range.max));
          this._render();
        }

        return;
      }

      const opener = event.target.closest('[data-afc-dialog-open]');

      if (opener) {
        this._openDialog(opener.dataset.afcDialogOpen);
        return;
      }

      // Close button, or a click on the backdrop (the <dialog> itself, outside its panel)
      if (event.target.closest('[data-afc-dialog-close]') || event.target instanceof HTMLDialogElement) {
        event.target.closest('dialog')?.close();
        return;
      }

      const thumb = event.target.closest('[data-afc-thumb]');

      if (thumb) {
        const image = this._galleryMedia[Number(thumb.dataset.afcThumb)];

        // In an option's pictures, the first thumbnail goes back to the fireplace
        if (image?.fireplace) {
          this._galleryStep = null;
          this._renderGallery();
          return;
        }

        this._showImage(image);
        this.querySelectorAll('[data-afc-thumb]').forEach((candidate) => candidate.setAttribute('aria-current', String(candidate === thumb)));
        return;
      }

      if (event.target.closest('[data-afc-add]')) {
        this._addToCart();
        return;
      }

      const shareButton = event.target.closest('[data-afc-share-action]');

      if (shareButton) {
        this._share(shareButton.dataset.afcShareAction);
      }
    }

    /* ---------------------------------------------------------------------------------------------------------------
     * Save and share: the link holds the whole build, so sharing is sharing the link
     * ------------------------------------------------------------------------------------------------------------ */

    get _shareTitle() {
      return `My ${this._buildName}`;
    }

    // Plain-text build summary for emails and the share sheet
    get _shareText() {
      const rows = this._rows();
      const lines = rows.map((row) => `- ${row.name}${row.label ? ` (${row.label})` : ''}: ${row.free ? 'Free' : this._money(row.price)}`);
      const ship = this._shipSentence;
      return `${this.dataset.shareIntro || 'Here is the Aquafire I configured:'}\n\n${this._buildName}\n${lines.join('\n')}\nTotal: ${this._money(totalOf(rows))}${ship ? `\n${ship}` : ''}`;
    }

    async _share(action) {
      const url = window.location.href;

      if (action === 'share' && navigator.share) {
        try {
          await navigator.share({ title: this._shareTitle, text: this._shareText, url });
        } catch (error) {
          // Closing the share sheet rejects; nothing to do.
        }
        return;
      }

      if (action === 'print') {
        this._renderPrintSheet();
        window.print();
        return;
      }

      try {
        await navigator.clipboard.writeText(url);
        this._shareStatus('Link copied. Paste it anywhere to come back to this build.');
      } catch (error) {
        window.prompt('Copy this link to your build:', url);
      }
    }

    _shareStatus(message) {
      const status = this.querySelector('[data-afc-share-status]');

      if (!status) {
        return;
      }

      status.textContent = message;
      status.hidden = false;
      clearTimeout(this._shareStatusTimer);
      this._shareStatusTimer = setTimeout(() => {
        status.hidden = true;
      }, 4000);
    }

    _renderShareLinks() {
      const subject = encodeURIComponent(this._shareTitle);
      const body = encodeURIComponent(`${this._shareText}\n\nView or change this build: ${window.location.href}`);

      this.querySelectorAll('[data-afc-share-email]').forEach((link) => {
        link.href = `mailto:?subject=${subject}&body=${body}`;
      });
    }

    // One-page build sheet for printing or saving as PDF: every cart line with its SKU, for dealers and designers.
    _renderPrintSheet() {
      const sheet = this._printSheet || (this._printSheet = this.querySelector('[data-afc-print]'));

      if (!sheet) {
        return;
      }

      // Printed on its own: moved to the top of the document, where print styles hide everything else.
      if (sheet.parentElement !== document.body) {
        document.body.append(sheet);
      }

      const rows = this._rows();
      const make = (tag, className, text) => {
        const element = document.createElement(tag);

        if (className) {
          element.className = className;
        }

        if (text !== undefined) {
          element.textContent = text;
        }

        return element;
      };

      const table = make('table', 'afc-print__table');
      const head = make('tr');
      ['Item', 'SKU', 'Qty', 'Price'].forEach((label) => head.append(make('th', '', label)));
      table.append(make('thead'), make('tbody'), make('tfoot'));
      table.tHead.append(head);

      rows.forEach((row) => {
        const group = make('tr', 'afc-print__group');
        const groupCell = make('td', '', row.label ? `${row.name} · ${row.label}` : row.name);
        groupCell.colSpan = 4;
        group.append(groupCell);
        table.tBodies[0].append(group);

        row.items.forEach((item) => {
          const line = make('tr');
          const title = item.variant.title === 'Default Title' ? item.product.title : `${item.product.title} – ${item.variant.title}`;
          line.append(make('td', '', title), make('td', '', item.variant.sku || ''), make('td', '', String(item.quantity)), make('td', '', row.free ? 'Free' : this._money(item.variant.price * item.quantity)));
          table.tBodies[0].append(line);
        });
      });

      const totalRow = make('tr');
      const totalLabel = make('td', '', 'Total');
      totalLabel.colSpan = 3;
      totalRow.append(totalLabel, make('td', '', this._money(totalOf(rows))));
      table.tFoot.append(totalRow);

      sheet.replaceChildren(
        make('p', 'afc-print__meta', [this.dataset.shopName, 'Build summary', new Date().toLocaleDateString()].filter(Boolean).join(' · ')),
        make('h1', 'afc-print__title', this._buildName),
        make('p', 'afc-print__specs', [this.state.model.features.join(' · '), this._shipSentence].filter(Boolean).join(' · ')),
        table,
        make('p', 'afc-print__link', `View or change this build online: ${window.location.href}`)
      );
    }

    _openDialog(id) {
      const dialog = [...this.querySelectorAll('dialog[data-afc-dialog]')].find((candidate) => candidate.dataset.afcDialog === id);

      if (!dialog) {
        return;
      }

      // Page content is kept in a <template> until first opened, so its images don't load with the page.
      const template = dialog.querySelector('template[data-afc-dialog-content]');

      if (template) {
        template.replaceWith(template.content.cloneNode(true));
      }

      dialog.showModal();
    }

    /* ---------------------------------------------------------------------------------------------------------------
     * Rendering
     * ------------------------------------------------------------------------------------------------------------ */

    _money(cents) {
      return formatMoney(cents, this.moneyFormat);
    }

    _buildSizeCards() {
      this.models.forEach((model) => {
        const list = this.querySelector(`[data-afc-sizes-for="${model.key}"] [data-afc-size-list]`);

        if (!list) {
          return;
        }

        const cards = model.sizes.map((entry) => {
          const label = document.createElement('label');
          const input = document.createElement('input');
          const body = document.createElement('span');
          const title = document.createElement('span');

          label.className = `afc-card afc-card--size${entry.ganged ? ' afc-card--ganged' : ''}`;
          label.dataset.afcSize = entry.key;
          input.className = 'afc-card__input';
          input.type = 'radio';
          input.name = `afc-size-${this.dataset.sectionId}`;
          input.value = entry.key;
          body.className = 'afc-card__body';
          title.className = 'afc-card__title';
          title.textContent = entry.title;
          body.append(title);

          if (entry.note) {
            const note = document.createElement('span');
            note.className = 'afc-card__text';
            note.textContent = entry.note;
            body.append(note);
          }

          const price = document.createElement('span');
          price.className = 'afc-card__price';
          price.dataset.afcPrice = '';
          body.append(price);
          label.append(input, body);
          entry.element = label;
          return label;
        });

        // A light divider between the single sizes and the ganged ones (two units side by side)
        const firstGanged = model.sizes.findIndex((entry) => entry.ganged);

        if (firstGanged > 0) {
          const divider = document.createElement('p');
          divider.className = 'afc-size-divider';

          if (this.gangedLabel) {
            divider.textContent = this.gangedLabel;
          } else {
            divider.setAttribute('aria-hidden', 'true');
          }

          cards.splice(firstGanged, 0, divider);
        }

        list.replaceChildren(...cards);
      });
    }

    _buildCompareTable() {
      const container = this.querySelector('[data-afc-compare-table]');

      if (!container) {
        return;
      }

      const labels = [];
      this.models.forEach((model) => model.specs.forEach(([label]) => labels.includes(label) || labels.push(label)));

      if (labels.length === 0) {
        this.querySelectorAll('[data-afc-dialog-open="compare"]').forEach((opener) => {
          opener.hidden = true;
        });
        return;
      }

      const cell = (tag, text, scope) => {
        const element = document.createElement(tag);
        element.textContent = text;

        if (scope) {
          element.scope = scope;
        }

        return element;
      };

      const table = document.createElement('table');
      const head = document.createElement('thead');
      const headRow = document.createElement('tr');
      const body = document.createElement('tbody');

      headRow.append(cell('th', '', 'col'), ...this.models.map((model) => {
        const heading = cell('th', model.name.replace(/^Aquafire\s+/i, ''), 'col');

        if (model.compareImage) {
          const image = document.createElement('img');
          image.className = 'afc-compare__image';
          image.src = model.compareImage;
          image.alt = model.name;
          image.loading = 'lazy';
          image.width = 600;
          image.height = 450;
          heading.prepend(image);
        }

        return heading;
      }));
      head.append(headRow);
      body.append(...labels.map((label) => {
        const row = document.createElement('tr');
        row.append(cell('th', label, 'row'), ...this.models.map((model) => cell('td', model.specs.find(([key]) => key === label)?.[1] || '—')));
        return row;
      }));

      // A link to each model's product page, in a new tab so the build stays put
      const foot = document.createElement('tfoot');
      const footRow = document.createElement('tr');
      footRow.append(cell('td', ''), ...this.models.map((model) => {
        const td = document.createElement('td');

        if (model.product?.url) {
          const link = document.createElement('a');
          link.className = 'afc-compare__link link';
          link.href = model.product.url;
          link.target = '_blank';
          link.rel = 'noopener';
          link.textContent = 'View details';
          link.setAttribute('aria-label', `${model.name} details (opens in a new tab)`);
          link.insertAdjacentHTML('beforeend', '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 17 17 7"/><path d="M8 7h9v9"/></svg>');
          td.append(link);
        }

        return td;
      }));
      foot.append(footRow);

      table.className = 'afc-compare';
      table.append(head, body, foot);
      container.replaceChildren(table);
    }

    _render() {
      const model = this.state.model;
      const size = this._size;

      // Model step
      this.models.forEach((candidate) => {
        const selected = candidate === model;
        const buyable = candidate.sizes.filter((entry) => !entry.ganged && entry.available);
        const lowestPrice = Math.min(...(buyable.length > 0 ? buyable : candidate.sizes.filter((entry) => !entry.ganged)).map((entry) => entry.price));
        candidate.element.hidden = !candidate.sizes.some((entry) => entry.available);
        candidate.input.checked = selected;
        candidate.element.classList.toggle('is-selected', selected);
        candidate.element.querySelector('[data-afc-price]').textContent = `From ${this._money(lowestPrice)}`;
      });

      // Size step: only the selected model's sizes
      this.querySelectorAll('[data-afc-sizes-for]').forEach((element) => {
        element.hidden = element.dataset.afcSizesFor !== model.key;
      });

      // After the model, cards show price differences, as on apple.com ("+ $2,650.00", "− $250.00"): a size, the
      // fireplace's own difference; an option, what picking it does to the total
      const total = this._buildTotal();

      model.sizes.forEach((entry) => {
        if (!entry.element) {
          return;
        }

        const selected = entry === size;
        entry.element.querySelector('input').checked = selected;
        entry.element.hidden = !entry.available;
        entry.element.classList.toggle('is-selected', selected);
        this._setPrice(entry.element, selected ? '' : this._delta(entry.price - size.price));
      });

      // The ganged-sizes divider goes with the ganged sizes
      this.querySelectorAll(`[data-afc-sizes-for="${model.key}"] .afc-size-divider`).forEach((divider) => {
        divider.hidden = !model.sizes.some((entry) => entry.ganged && entry.available);
      });

      const gangedNote = this.querySelector('[data-afc-ganged-note]');
      gangedNote.hidden = !size.ganged;
      gangedNote.textContent = size.ganged ? `Your ${this._buildName} is ${this._unitsSentence(size)} installed side by side as one continuous flame. ${this.gangedNote}`.trim() : '';

      // Upgrade steps
      this.steps.forEach((step) => {
        const visible = this._stepVisible(step);
        const picks = this._picks(step);
        step.element.hidden = !visible;

        if (!visible) {
          return;
        }

        if (step.noneElement) {
          const selected = picks.size === 0;
          step.noneElement.querySelector('input').checked = selected;
          step.noneElement.classList.toggle('is-selected', selected);
          this._setPrice(step.noneElement, selected ? '' : this._delta(this._totalWith((state) => { state.picks.get(step.key).clear(); }) - total));
        }

        step.options.forEach((option) => {
          const state = this._optionState(option);
          const active = this._isActive(option, state);

          option.element.hidden = !state.visible;

          if (!state.visible) {
            return;
          }

          if (option.input) {
            option.input.checked = active;
            option.input.disabled = !state.enabled;
          }

          option.element.classList.toggle('is-selected', active);
          option.element.classList.toggle('is-disabled', !state.enabled);
          this._setNote(option.element, this._optionNote(option, state, active));

          if (option.free) {
            this._setFreePrice(option.element, state.resolved.price);
          } else {
            this._setPrice(option.element, this._optionPrice(option, state, active, total));
          }

          option.element.querySelector('[data-afc-variant-title]').textContent = option.chooseVariant ? '' : state.resolved.label;
          this._renderQuantity(option, active);
          this._renderChoice(option, active, state);
        });

        this._renderStepMedia(step);
      });

      this._renderSummary();
      this._writeUrl();
      this._renderShareLinks();
      this._renderModelIcons();
      this._announceModel();
      this._renderGallery();
    }

    // The selected model's card opens to show its feature icons, from its product page; the others close. The selected
    // model's page loads right away, the others once the page is idle, so switching shows them at once.
    _renderModelIcons() {
      const pages = window.AquafireProductPages;

      this.models.forEach((model) => {
        if (!model.iconsElement) {
          return;
        }

        if (model === this.state.model && !model.iconsRequested && pages) {
          this._loadModelIcons(model);
        }

        model.iconsElement.classList.toggle('is-open', model === this.state.model && Boolean(model.iconsHtml));
      });

      if (!this._prefetchedModels && pages && !navigator.connection?.saveData) {
        this._prefetchedModels = true;
        const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 2000));
        idle(() => this.models.forEach((model) => model.iconsElement && !model.iconsRequested && this._loadModelIcons(model)), { timeout: 5000 });
      }
    }

    _loadModelIcons(model) {
      const pages = window.AquafireProductPages;
      const cached = pages.cached(model.product.url);
      model.iconsRequested = true;

      // Remembered from an earlier visit: shown at once, without the opening animation
      if (cached) {
        this._fillModelIcons(model, cached.features, false);
      }

      pages.load(model.product.url)
        .then((parts) => this._fillModelIcons(model, parts.features, true))
        .catch(() => {
          // No icons for this model; its card simply doesn't open
        });
    }

    _fillModelIcons(model, html, animate) {
      const region = model.iconsElement;

      if (model.iconsHtml !== html) {
        model.iconsHtml = html;
        region.querySelector('[data-afc-model-icons-inner]').innerHTML = html;
      }

      const open = model === this.state.model && Boolean(html);

      if (!animate && open && !region.classList.contains('is-open')) {
        region.style.transition = 'none';
        region.classList.add('is-open');
        region.getBoundingClientRect();
        region.style.transition = '';
      }

      region.classList.toggle('is-open', open);
    }

    // The picked model, for the model details section below the configurator (assets/aquafire-model-details.js)
    get selectedModel() {
      const model = this.state?.model;
      return model ? { key: model.key, name: model.name, url: model.product.url, handle: model.product.handle } : null;
    }

    _announceModel() {
      if (this._announcedModel === this.state.model.key) {
        return;
      }

      this._announcedModel = this.state.model.key;
      this.dispatchEvent(new CustomEvent('aquafire:model-change', { bubbles: true, detail: this.selectedModel }));
    }

    // Phones: the step's large picture shows the option picked last, else another picked one (an included adapter plate,
    // say), else the first one on offer, so "None" still pictures what the step is about.
    _renderStepMedia(step) {
      if (step.mediaItems.length === 0) {
        return;
      }

      step.options.forEach((option) => {
        const item = step.mediaItems.find((candidate) => candidate.dataset.afcMedia === option.key);

        if (item) {
          this._arrangeMedia(option, item);
        }
      });

      const shown = step.options.filter((option) => !option.element.hidden);
      const picked = shown.filter((option) => option.element.classList.contains('is-selected'));
      const order = [...picked.filter((option) => option.key === step.lastPicked), ...picked, ...shown];
      const hasMedia = (option) => step.mediaItems.some((item) => item.dataset.afcMedia === option.key);
      const key = order.find(hasMedia)?.key;

      step.mediaItems.forEach((item) => {
        const shown = item.dataset.afcMedia === key;

        // An option's pictures start from the first each time it's shown again
        if (shown && item.hidden) {
          item.hidden = false;
          item.querySelector('[data-afc-media-track]').scrollLeft = 0;
          this._updateMediaDots(item);
        }

        item.hidden = !shown;
      });
    }

    // Several pictures for one option: a native swipe (scroll snapping) with dots that follow it; a dot shows its picture.
    _bindMediaCarousel(item) {
      const track = item.querySelector('[data-afc-media-track]');
      let frame = 0;

      if (!track) {
        return;
      }

      track.addEventListener('scroll', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this._updateMediaDots(item));
      }, { passive: true });

      item.addEventListener('click', (event) => {
        const dot = event.target.closest('[data-afc-media-dot]');

        if (dot) {
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          track.scrollTo({ left: Number(dot.dataset.afcMediaDot) * track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
        }
      });

      this._buildMediaDots(item);
    }

    // One dot per picture shown; a single picture has no dots and isn't announced as a carousel
    _buildMediaDots(item) {
      const track = item.querySelector('[data-afc-media-track]');
      const dots = item.querySelector('[data-afc-media-dots]');
      const count = track.querySelectorAll('[data-afc-media-slide]:not([hidden])').length;

      dots?.replaceChildren(...Array.from({ length: count > 1 ? count : 0 }, (_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'afc-step__media-dot';
        dot.dataset.afcMediaDot = String(index);
        dot.setAttribute('aria-label', `Picture ${index + 1} of ${count}`);
        return dot;
      }));

      if (count > 1) {
        track.tabIndex = 0;
        track.setAttribute('role', 'group');
        track.setAttribute('aria-roledescription', 'carousel');
        track.setAttribute('aria-label', item.dataset.afcMediaLabel || '');
      } else {
        track.removeAttribute('tabindex');
        track.removeAttribute('role');
        track.removeAttribute('aria-roledescription');
        track.removeAttribute('aria-label');
      }

      this._updateMediaDots(item);
    }

    // The sizes an option's pictures should show, for options sized to the fireplace: the numbers in the variant the
    // cart gets (the firebox "52 in. Firebox (fits 40 in. Aquafire)", ClearShield "40 Inch"), or else the fireplace
    // sizes (the logs, whose pictures come from a listing with "For 40" burner" variants). null: any picture fits.
    _pictureSizes(option) {
      if (!['unit_size', 'total_size', 'length'].includes(option.match) || !option.product || !this._size) {
        return null;
      }

      const resolved = this._resolve(option);

      if (!resolved) {
        return null;
      }

      const own = resolved.items.filter((item) => item.product?.id === option.product.id);
      const numbers = own.length > 0 ? own.flatMap((item) => numbersIn(item.variant.title)) : this._size.units.map((unit) => unit.size);
      return new Set(numbers.filter(Boolean));
    }

    // Which of an option's pictures show, in what order: up to its "Pictures" count, in the product's order. For a sized
    // option, a picture whose file name carries one of the product's sizes (AquafireBox_72_fire, 40_Aquafirelogset,
    // crystalshield-glass-60-in) only shows for that size, and the first one of the picked size moves to the front.
    _arrangeMedia(option, item) {
      const track = item.querySelector('[data-afc-media-track]');
      const slides = [...track.querySelectorAll('[data-afc-media-slide]')]
        .sort((a, b) => Number(a.dataset.afcMediaSlide) - Number(b.dataset.afcMediaSlide));
      const sizes = this._pictureSizes(option);
      let order = slides;

      if (sizes) {
        const productSizes = new Set(option.product.variants.flatMap((variant) => numbersIn(variant.title)));
        const found = (slide) => fileNumbers(slide.querySelector('img')?.getAttribute('src')).filter((number) => productSizes.has(number));
        const kept = slides.filter((slide) => found(slide).length === 0 || found(slide).some((number) => sizes.has(number)));
        // Sizes in order, so a ganged 100" (60" + 40") leads with the 60" picture
        const first = [...sizes].map((size) => kept.find((slide) => found(slide).includes(size))).find(Boolean);
        order = kept.length === 0 ? slides : first ? [first, ...kept.filter((slide) => slide !== first)] : kept;
      }

      const shown = order.slice(0, Number(item.dataset.afcMediaLimit) || 1);
      const signature = shown.map((slide) => slide.dataset.afcMediaSlide).join(',');

      if (item.dataset.afcMediaShown === signature) {
        return;
      }

      item.dataset.afcMediaShown = signature;
      slides.forEach((slide) => {
        slide.hidden = !shown.includes(slide);
      });
      shown.forEach((slide) => track.append(slide));
      track.scrollLeft = 0;
      this._buildMediaDots(item);

      // Desktop: the card's small picture is the size's too
      const thumb = option.element.querySelector('.afc-card__thumb');
      const picture = shown[0]?.querySelector('img');

      if (sizes && thumb && picture) {
        thumb.src = picture.getAttribute('src');
        const srcset = picture.getAttribute('srcset');

        if (srcset) {
          thumb.setAttribute('srcset', srcset);
        }
      }
    }

    _updateMediaDots(item) {
      const track = item.querySelector('[data-afc-media-track]');
      const index = Math.round(Math.abs(track.scrollLeft) / Math.max(track.clientWidth, 1));

      item.querySelectorAll('[data-afc-media-dot]').forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === index);
        dot.toggleAttribute('aria-current', dotIndex === index);
      });
    }

    _setNote(card, text) {
      const note = card.querySelector('[data-afc-note]');

      if (note) {
        note.textContent = text;
        note.hidden = !text;
      }
    }

    _setPrice(card, text) {
      const price = card.querySelector('[data-afc-price]');
      price.textContent = text;
      price.hidden = !text;
    }

    // A free part's card, as Lectric and others show included accessories: what it's worth, struck through, then "Free"
    _setFreePrice(card, cents) {
      const price = card.querySelector('[data-afc-price]');
      const value = document.createElement('s');
      const hint = document.createElement('span');

      hint.className = 'afc-visually-hidden';
      hint.textContent = 'Worth ';
      value.className = 'afc-card__value';
      value.append(hint, formatMoney(cents, this.dataset.shopMoneyFormat || this.moneyFormat));
      price.replaceChildren(value, ' Free');
      price.hidden = false;
    }

    // An option card's price. One-choice steps: what picking it changes (nothing shown on the one picked). Add-ons
    // (several choices) and parts that come with another pick: what they add.
    _optionPrice(option, state, active, total) {
      // Nothing on a card that can't be picked (ClearShield with logs)
      if (!state.enabled) {
        return '';
      }

      if (option.included || option.step.mode !== 'single') {
        return this._delta(state.resolved.price);
      }

      if (active) {
        return '';
      }

      return this._delta(this._totalWith((draft) => {
        const picks = draft.picks.get(option.step.key);
        picks.clear();
        picks.add(option.key);
      }) - total);
    }

    _buildTotal() {
      return totalOf(this._rows());
    }

    // The total if a change were made (another size or pick), with picks that would no longer apply dropped
    _totalWith(change) {
      const { sizeKey, picks } = this.state;
      this.state.picks = new Map([...picks].map(([key, set]) => [key, new Set(set)]));

      try {
        change(this.state);
        this._normalize();
        return this._buildTotal();
      } finally {
        this.state.sizeKey = sizeKey;
        this.state.picks = picks;
      }
    }

    // "+ $2,650.00" / "− $250.00" in the shop's format without the currency code, like apple.com; '' for no change
    _delta(cents) {
      if (!cents) {
        return '';
      }

      return `${cents > 0 ? '+' : '\u2212'} ${formatMoney(Math.abs(cents), this.dataset.shopMoneyFormat || this.moneyFormat)}`;
    }

    // The variant choice on a picked card: one button per variant that can be bought, the one in the build pressed
    _renderChoice(option, active, state) {
      const group = option.choiceElement;

      if (!group) {
        return;
      }

      const pickedId = String(state.resolved.items[0]?.variant.id);
      group.hidden = !active;

      group.querySelectorAll('[data-afc-choice-variant]').forEach((button) => {
        const variant = option.product.variants.find((candidate) => String(candidate.id) === button.dataset.afcChoiceVariant);
        button.hidden = !variant || !canBuy(variant, 1);
        button.setAttribute('aria-pressed', String(button.dataset.afcChoiceVariant === pickedId));
      });
    }

    _renderQuantity(option, active) {
      const stepper = option.quantityElement;
      const range = this._quantityRange(option);

      if (!stepper) {
        return;
      }

      stepper.hidden = !active || !range || range.max < 2;

      if (stepper.hidden) {
        return;
      }

      const quantity = this._quantity(option);
      stepper.querySelector('[data-afc-qty-value]').textContent = String(quantity);
      stepper.querySelector('[data-afc-qty-step="-1"]').disabled = quantity <= range.min;
      stepper.querySelector('[data-afc-qty-step="1"]').disabled = quantity >= range.max;
      stepper.querySelector('[data-afc-qty-hint]').textContent = `Up to ${range.max} for ${this._size.total}" of flame`;
    }

    _unitsSentence(size) {
      const sizes = size.units.map((unit) => unit.size);
      const count = NUMBER_WORDS[sizes.length] || String(sizes.length);

      if (sizes.every((inches) => inches === sizes[0])) {
        return `${count} ${sizes[0]}" fireplaces`;
      }

      return `${sizes.map((inches) => `a ${inches}"`).join(' and ')} fireplace`;
    }

    _renderSummary() {
      const rows = this._rows();
      const plan = this._shipPlan(rows);
      const list = this.querySelector('[data-afc-summary-list]');
      let total = 0;
      let compareAtTotal = 0;

      // One tile per part: icon, name, detail and price
      list.replaceChildren(...rows.map((summaryRow) => {
        const row = document.createElement('li');
        const name = document.createElement('span');
        const price = document.createElement('span');

        // "You save" counts compare-at prices and what free parts are worth (the $265 Vapor Pure)
        total += summaryRow.price;
        compareAtTotal += summaryRow.items.reduce((sum, item) => sum + Math.max(item.variant.compare_at_price, item.variant.price) * item.quantity, 0);

        row.className = 'afc-review__item';
        name.className = 'afc-review__name';
        name.textContent = summaryRow.name;
        row.append(iconElement(summaryRow.icon), name);

        if (summaryRow.label) {
          const detail = document.createElement('span');
          detail.className = 'afc-review__detail';
          detail.textContent = summaryRow.label;
          row.append(detail);
        }

        price.className = 'afc-review__price';
        price.textContent = summaryRow.free ? 'Free' : this._money(summaryRow.price);
        row.append(price);

        const separate = plan.separate.find((entry) => entry.row === summaryRow);

        if (this.ship.enabled && separate) {
          const ship = document.createElement('span');
          ship.className = 'afc-review__ship';
          ship.textContent = separate.ship.end ? `Ships separately: ${this._formatShip(separate.ship)}` : 'Ships separately, date to be confirmed';
          row.append(ship);
        }

        return row;
      }));

      this._renderShip(rows);

      this.querySelectorAll('[data-afc-model-name]').forEach((element) => {
        element.textContent = this.state.model.name;
      });

      this.querySelectorAll('[data-afc-build-specs]').forEach((element) => {
        element.textContent = this.state.model.features.join(' · ');
        element.hidden = this.state.model.features.length === 0;
      });

      this.querySelectorAll('[data-afc-total]').forEach((element) => {
        element.textContent = this._money(total);
      });

      this._renderInstallments(total);

      this.querySelectorAll('[data-afc-build-name]').forEach((element) => {
        element.textContent = this._buildName;
      });

      const savings = this.querySelector('[data-afc-savings]');
      savings.hidden = compareAtTotal <= total;
      savings.textContent = compareAtTotal > total ? `You save ${this._money(compareAtTotal - total)}` : '';

      const canAdd = Boolean(this._size?.available);

      this.querySelectorAll('[data-afc-add]').forEach((button) => {
        button.disabled = !canAdd;
      });
    }

    // The build's estimated ship date: when its latest part ships.
    // The build's ship dates. The fireplace's date is the main one; an add-on ships separately when its date differs
    // and one of them is delayed: an out-of-stock firebox with a fireplace in stock, or in-stock logs with a fireplace
    // waiting on a shipment. Small differences between parts in stock don't count.
    _shipPlan(rows) {
      const main = this._shipOfItems(rows[0].items);
      const sameWindow = (a, b) => a.start?.getTime() === b.start?.getTime() && a.end?.getTime() === b.end?.getTime();
      const separate = rows.slice(1)
        .map((row) => ({ row, name: row.name, ship: this._shipOfItems(row.items) }))
        .filter(({ ship }) => (ship.backordered || main.backordered) && !sameWindow(ship, main));

      return { main, separate };
    }

    // "AquafireBox and Logs ship separately: Nov 12 – Nov 26. ClearShield ships separately; we'll email you its ship date."
    _separateShipText(separate) {
      const groups = new Map();

      separate.forEach(({ name, ship }) => {
        const key = ship.end ? this._formatShip(ship) : '';
        groups.set(key, [...(groups.get(key) || []), name]);
      });

      return [...groups].map(([window, names]) => {
        const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names.at(-1)}` : names[0];
        const verb = names.length > 1 ? 'ship' : 'ships';
        return window ? `${list} ${verb} separately: ${window}.` : `${list} ${verb} separately; we'll email you ${names.length > 1 ? 'their' : 'its'} ship date.`;
      }).join(' ');
    }

    _renderShip(rows) {
      const container = this.querySelector('[data-afc-ship]');
      const barShip = this.querySelector('[data-afc-bar-ship]');
      const barExtra = this.querySelector('[data-afc-bar-ship-extra]');

      if (!container) {
        return;
      }

      container.hidden = !this.ship.enabled;
      barShip?.toggleAttribute('hidden', !this.ship.enabled);
      barExtra?.toggleAttribute('hidden', true);

      if (!this.ship.enabled) {
        return;
      }

      const { main, separate } = this._shipPlan(rows);
      const mainWindow = main.end ? this._formatShip(main) : '';
      const text = container.querySelector('[data-afc-ship-text]');
      const pieces = [];

      container.querySelector('[data-afc-ship-title]').textContent = main.end ? `Estimated ship date: ${mainWindow}` : 'Ship date to be confirmed';

      if (!main.end) {
        pieces.push('We\'ll email you a ship date.');
      }

      if (separate.length > 0) {
        pieces.push(this._separateShipText(separate));
      } else if (!main.backordered) {
        pieces.push('Everything is in stock.');
      }

      text.textContent = pieces.join(' ');
      text.hidden = !text.textContent;

      // Sticky bar: the fireplace's date, and a second line when add-ons ship separately
      if (barShip) {
        if (separate.length === 0) {
          barShip.textContent = main.end ? `Ships ${mainWindow}` : 'Ship date to be confirmed';
        } else {
          const fireplaces = this._size.units.length > 1;
          barShip.textContent = main.end
            ? `${fireplaces ? 'Fireplaces ship' : 'Fireplace ships'} ${mainWindow}`
            : `${fireplaces ? 'Fireplaces' : 'Fireplace'}: ship date to be confirmed`;
        }
      }

      if (barExtra && separate.length > 0) {
        const [only] = separate;
        barExtra.textContent = separate.length > 1
          ? `${separate.length} add-ons ship separately`
          : only.ship.end ? `${only.name} ships ${this._formatShip(only.ship)}` : `${only.name}: date to be confirmed`;
        barExtra.hidden = false;
      }
    }

    get _shipSentence() {
      if (!this.ship.enabled) {
        return '';
      }

      const { main, separate } = this._shipPlan(this._rows());
      const sentence = main.end ? `Estimated ship date: ${this._formatShip(main)}` : 'Ship date to be confirmed';
      return separate.length > 0 ? `${sentence}. ${this._separateShipText(separate)}` : sentence;
    }

    // Shop Pay Installments banner for the build total. Shopify's banner element (<shopify-payment-terms>, defined by
    // its script) has a cart mode priced from its data; it gets the shop's financing plans from the product banner
    // Shopify printed into the template, and a new element is made whenever the total changes.
    _renderInstallments(total) {
      const container = this.querySelector('[data-afc-installments]');

      if (!container) {
        return;
      }

      if (this._installmentsMeta === undefined) {
        const source = container.querySelector('template[data-afc-installments-source]')?.content.querySelector('shopify-payment-terms');
        this._installmentsMeta = null;

        try {
          const meta = JSON.parse(source?.getAttribute('shopify-meta') || 'null');
          this._installmentsMeta = meta && Array.isArray(meta.financing_plans) ? meta : null;
        } catch (error) {
          console.error('[aquafire-configurator] Could not read Shop Pay Installments data', error);
        }
      }

      const meta = this._installmentsMeta;
      const amount = total / 100;
      const eligible = Boolean(meta) && amount >= meta.min_price_amount && amount <= meta.max_price_amount;

      container.hidden = !eligible;

      if (!eligible || this._installmentsTotal === total) {
        return;
      }

      const format = this.dataset.shopMoneyFormat || this.moneyFormat;
      const count = 4;
      const banner = document.createElement('shopify-payment-terms');

      banner.setAttribute('shopify-meta', JSON.stringify({
        type: 'cart',
        currency_code: meta.currency_code,
        country_code: meta.country_code,
        min_price: meta.min_price,
        min_price_amount: meta.min_price_amount,
        max_price: meta.max_price,
        max_price_amount: meta.max_price_amount,
        financing_plans: meta.financing_plans,
        installments_buyer_prequalification_enabled: meta.installments_buyer_prequalification_enabled,
        seller_id: meta.seller_id,
        eligible: true,
        full_price: formatMoney(total, format),
        full_price_amount: amount,
        number_of_payment_terms: count,
        price_per_term: formatMoney(Math.floor(total / count), format),
        price_per_term_amount: Math.floor(total / count) / 100
      }));

      this._installmentsTotal = total;
      container.querySelector('shopify-payment-terms')?.remove();
      container.append(banner);
    }

    // The main picture shows the selected model's product photos. Variant images are not used: on the fireplaces they
    // are small flame-color thumbnails, not photos. On desktop, picking an option shows that option's pictures instead
    // (the same ones the step shows on phones), with the fireplace as the first thumbnail to go back. Changing the
    // model or size, or picking "None", shows the fireplace again.
    _renderGallery() {
      const option = this._galleryOption();
      const item = option && option.step.mediaItems.find((candidate) => candidate.dataset.afcMedia === option.key);
      const signature = `${this.state.model.key}|${option?.key ?? ''}|${item?.dataset.afcMediaShown ?? ''}`;

      if (signature === this._gallerySignature) {
        return;
      }

      const first = this._gallerySignature === undefined;
      const fireplace = this.state.model.product.media;
      const media = option
        ? [{ ...fireplace[0], fireplace: true }, ...this._optionMedia(option)].filter((image) => image.src)
        : fireplace;
      const thumbs = this.querySelector('[data-afc-thumbs]');
      const current = option ? 1 : 0;

      this._gallerySignature = signature;
      this._galleryMedia = media;

      thumbs.replaceChildren(...media.map((image, index) => {
        const button = document.createElement('button');
        const img = document.createElement('img');
        button.type = 'button';
        button.className = 'afc-gallery__thumb';
        button.classList.toggle('is-product', Boolean(option) && !image.fireplace);
        button.dataset.afcThumb = String(index);
        button.setAttribute('aria-label', image.fireplace ? `Show the ${this.state.model.name}` : `Show image ${index + 1 - current} of ${media.length - current}`);
        button.setAttribute('aria-current', String(index === current));
        img.src = image.thumb;
        img.alt = '';
        img.loading = 'lazy';
        button.append(img);
        return button;
      }));
      thumbs.hidden = media.length < 2;

      this._showImage(media[current], !first);
    }

    // The step changed last on desktop pictures the option picked last there, else another picked one with pictures
    _galleryOption() {
      const step = this._galleryStep;

      if (!step || step.element.hidden) {
        return null;
      }

      const picked = step.options.filter((option) => !option.element.hidden && option.element.classList.contains('is-selected'));
      const order = [...picked.filter((option) => option.key === step.lastPicked), ...picked];
      return order.find((option) => step.mediaItems.some((item) => item.dataset.afcMedia === option.key)) ?? null;
    }

    // An option's pictures as arranged for the size, read from its phone picture panel: the largest size for the stage, a small one for its thumbnail
    _optionMedia(option) {
      const item = option.step.mediaItems.find((candidate) => candidate.dataset.afcMedia === option.key);
      const resize = (url, width) => url.replace(/([?&]width=)\d+/, `$1${width}`);

      return [...item.querySelectorAll('[data-afc-media-slide]:not([hidden]) .afc-step__media-image')].map((img) => {
        const sources = (img.getAttribute('srcset') || '').split(',').map((entry) => entry.trim().split(/\s+/)).filter(([url]) => url);
        const largest = sources.sort((a, b) => parseInt(b[1], 10) - parseInt(a[1], 10))[0]?.[0] || img.getAttribute('src');
        return { src: largest, thumb: resize(largest, 200), alt: img.getAttribute('alt') || option.name || '', product: true };
      });
    }

    _showImage(image, fade = false) {
      const stage = this.querySelector('[data-afc-stage]');
      const img = stage?.querySelector('img');

      if (!img || !image) {
        return;
      }

      // Accessory photos are shot on white: shown whole, like the phone picture panel, rather than cropped to fill
      stage.classList.toggle('is-product', Boolean(image.product));

      if (fade && img.getAttribute('src') !== image.src && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        img.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: 'ease-out' });
      }

      if (img.getAttribute('src') !== image.src) {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = image.src;
      }

      img.alt = image.alt || this.state.model.name;
      img.hidden = false;
    }

    // The sticky bar repeats the total and button; it hides while the full summary is on screen.
    _observeSummary() {
      const summary = this.querySelector('[data-afc-summary]');
      const bar = this.querySelector('[data-afc-bar]');

      if (!summary || !bar || !('IntersectionObserver' in window)) {
        return;
      }

      this._summaryObserver = new IntersectionObserver(([entry]) => {
        bar.classList.toggle('is-hidden', entry.isIntersecting);
      });
      this._summaryObserver.observe(summary);
    }

    _setError(message) {
      const error = this.querySelector('[data-afc-error]');
      error.textContent = message;
      error.hidden = !message;
    }

    /* ---------------------------------------------------------------------------------------------------------------
     * Cart
     * ------------------------------------------------------------------------------------------------------------ */

    // Other apps add their lines a moment after the build is added: the tariff & fuel surcharge (Upcharge), the free
    // Vapor Pure with a Pro or a Direct Plumb Kit. The drawer was drawn before that, so look at the cart a few more times
    // over the next 5 seconds and have Impact redraw the drawer whenever it changed.
    async _refreshWhenAppsChangeCart(root, cart) {
      const signature = (state) => `${state.item_count}|${state.total_price}|${(state.items || []).map((item) => `${item.key}:${item.quantity}`).join(',')}`;
      let last = signature(cart);

      for (const delay of AquafireConfigurator.cartRecheckDelays) {
        await new Promise((resolve) => setTimeout(resolve, delay));

        try {
          const current = signature(await (await fetch(`${root}cart.js`)).json());

          if (current !== last) {
            last = current;
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          }
        } catch (error) {
          return;
        }
      }
    }

    async _addToCart() {
      const rows = this._rows();

      if (this._submitting || !this._size?.available) {
        return;
      }

      const buttons = this.querySelectorAll('[data-afc-add]');
      const root = window.Shopify?.routes?.root || '/';
      const buildId = Math.random().toString(36).slice(2, 6).toUpperCase();
      const buildLabel = `${this._buildName} · #${buildId}`;
      const sections = [];

      this._submitting = true;
      this._setError('');
      buttons.forEach((button) => button.setAttribute('aria-busy', 'true'));

      // The cart drawer adds its section ID here so the response carries its fresh HTML.
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));

      const body = {
        // Free parts aren't added here: the app that gives them away adds them when the fireplace or kit lands in the cart
        items: rows.filter((row) => !row.free).flatMap((row) => row.items.map((item) => {
          const properties = {
            [this.dataset.buildProperty || 'Build']: buildLabel,
            _build_id: buildId,
            _build_part: row.part
          };
          const ship = this._shipOf(item);

          // Out-of-stock lines carry their estimate into the cart, checkout and order
          if (this.ship.enabled && ship.backordered) {
            properties[this.ship.property] = ship.end ? this._formatShip(ship) : 'To be confirmed';
          }

          if (this.ship.enabled && ship.end) {
            properties._ship_by = `${ship.end.getFullYear()}-${String(ship.end.getMonth() + 1).padStart(2, '0')}-${String(ship.end.getDate()).padStart(2, '0')}`;
          }

          return { id: item.variant.id, quantity: item.quantity, properties };
        }))
      };

      if (sections.length > 0) {
        body.sections = sections.join(',');
        body.sections_url = window.location.pathname;
      }

      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body)
        });
        const responseJson = await response.json();

        if (!response.ok) {
          this._setError(responseJson.description || responseJson.message || 'This build could not be added to your cart. Please try again.');
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          return;
        }

        if (this.dataset.cartType !== 'drawer' || window.themeVariables?.settings?.pageType === 'cart') {
          window.location.href = `${root}cart`;
          return;
        }

        const cart = await (await fetch(`${root}cart.js`)).json();
        cart.sections = responseJson.sections || {};

        this.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: { items: responseJson.items || [responseJson], cart }
        }));

        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart }
        }));

        this._refreshWhenAppsChangeCart(root, cart);
      } catch (error) {
        console.error('[aquafire-configurator] Add to cart failed', error);
        this._setError('This build could not be added to your cart. Please check your connection and try again.');
      } finally {
        this._submitting = false;
        buttons.forEach((button) => button.removeAttribute('aria-busy'));
      }
    }
  }

  AquafireConfigurator.sizeOf = sizeOf;
  AquafireConfigurator.cartRecheckDelays = [700, 1500, 3000];
  AquafireConfigurator.formatMoney = formatMoney;

  window.customElements.define('aquafire-configurator', AquafireConfigurator);
})();
